/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
import { defineNuxtPlugin, useHead, useRuntimeConfig } from 'nuxt/app'
import { installDeferredObserver } from '~/utility/deferred-observer'

const _layer = 'dataLayer'

function gtmClient() {
  return {
    push(obj) {
      if (!window[_layer]) {
        window[_layer] = []
      }
      window[_layer].push(obj)
    },
  }
}

function gtmServer() {
  const events: any[] = []
  return {
    push(obj: any) {
      events.push(obj)
    },
    unshift(obj: any) {
      events.unshift(obj)
    },
    render(_gtmId: string) {
      let script =
        'window.gmo = window.gmo || {}; window.dataLayer = window.dataLayer || [];'
      while (events.length) {
        const event = events.shift()
        script += `${_layer}.push(${JSON.stringify(event)});`
      }
      // Only render the dataLayer initialization script during SSR.
      // The actual GTM container is loaded client-side after hydration
      // to improve web vitals (LCP, INP, TBT).
      useHead({
        script: [
          {
            id: 'data-layer',
            innerHTML: script,
          },
        ],
      })
    },
  }
}

/**
 * Load the GTM container script. Pushes the gtm.start event to the
 * dataLayer and creates the async script element.
 * Guarded against double-loading.
 */
function loadGtmScript(gtmId: string) {
  if ((window as any).__gtmLoaded) return
  ;(window as any).__gtmLoaded = true

  // Push GTM start event (same as the original inline snippet)
  window[_layer] = window[_layer] || []
  window[_layer].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `/_metrics/gtm.js?id=${gtmId}`
  document.head.appendChild(script)
}

/**
 * Defer GTM loading for better web vitals.
 * Loads on first user interaction OR after idle timeout — whichever comes first.
 * dataLayer events pushed before GTM loads are queued and processed on load.
 */
function deferGtmLoading(gtmId: string) {
  const interactionEvents = ['mousedown', 'touchstart', 'scroll', 'keydown']

  const load = () => {
    interactionEvents.forEach((e) => document.removeEventListener(e, load))
    loadGtmScript(gtmId)
  }

  interactionEvents.forEach((e) => {
    document.addEventListener(e, load, { once: true, passive: true })
  })

  // Fallback: load during idle time with 5s timeout.
  // Ensures GTM loads even if user doesn't interact.
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => load(), { timeout: 5000 })
  } else {
    setTimeout(load, 3500)
  }
}

function startPageTracking(gtm) {
  const router = useRouter()
  const runtimeConfig = useRuntimeConfig()
  router.afterEach((to) => {
    setTimeout(() => {
      gtm.push(
        to.meta.gtm || {
          routeName: to.name,
          pageType: 'PageView',
          pageUrl: runtimeConfig.public.envUrl + to.fullPath,
          pageTitle: (typeof document !== 'undefined' && document.title) || '',
          event: 'nuxtRoute',
        },
      )
    }, 250)
  })
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const gtm = process.client ? gtmClient() : gtmServer()

  // Make GTM available to Vue instance
  Object.defineProperty(nuxtApp.vueApp.config.globalProperties, '$gtm', {
    value: gtm,
    writable: false,
    configurable: true,
    enumerable: true,
  })

  gtm.push({
    isSSR: process.server,
  })

  if (process.server) {
    nuxtApp.hooks.hook('app:rendered', () => {
      const gtmServer = gtm as ReturnType<typeof gtmServer>
      gtmServer.unshift({
        alfredVersion: config.public.alfredVersion,
        config: config.public,
        event: 'appInit',
      })
      if (config.public.gtm && config.public.gtm.id && gtmServer.render) {
        gtmServer.render(config.public.gtm.id)
      }
    })
  }

  // Client-side: install performance hooks and defer GTM loading.
  // Hooks must be installed before GTM loads so all GTM-loaded scripts
  // (analytics, remarketing, etc.) go through our deferred MO/rAF Proxy.
  if (process.client) {
    const gtmId = config.public.gtm?.id as string | undefined
    if (gtmId) {
      installDeferredObserver()
      deferGtmLoading(gtmId)
    }
  }

  startPageTracking(gtm)
  return {
    provide: {
      gtm,
    },
  }
})
